import React, { useEffect, useMemo, useRef } from 'react';

import { CustomEventNames, EventTypes, logRBIEvent } from 'features/analytics';
import { InvalidItemsDiagnosticsPanel } from 'features/cart/components/cart-debug/invalid-items-diagnostics-panel';
import { CartEntry } from 'features/cart/components/cart-entry';
import { useCartContext } from 'features/cart/hooks/use-cart-context';
import { useInvalidItemsModalDiagnostics } from 'features/cart/hooks/use-invalid-items-modal-diagnostics';
import { iosCartBoldWeight } from 'features/cart/styles';
import { ICartEntry } from 'features/cart/types';
import { noop } from 'lodash-es';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import ActionButton from 'components/action-button';
import { ModalContent } from 'components/modal';
import { Header } from 'components/ui/header';
import { InlineModal } from 'components/ui/inline-modal';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import logger from 'utils/logger';

type CartInvalidItemsModalProps = {
  isOpen: boolean;
  cartEntries: ICartEntry[];
  offers: ICartEntry[];
  rewards: ICartEntry[];
  onConfirm: () => void;
};

const Section = ({ title, items }: { title?: string; items: ICartEntry[] }) => {
  if (!items.length) {
    return null;
  }
  return (
    <View style={styles.section}>
      {title && (
        <Typography variant="headerOne" style={styles.sectionTitle}>
          {title}
        </Typography>
      )}
      <View style={styles.entries}>
        {items.map(item => (
          <CartEntry key={item.cartId ?? item.id} cartEntry={item} readOnly={true} />
        ))}
      </View>
    </View>
  );
};

export const CartInvalidItemsModal = ({
  isOpen,
  cartEntries,
  offers,
  rewards,
  onConfirm,
}: CartInvalidItemsModalProps) => {
  const { isDiagnosticsEnabled } = useCartContext();
  const {
    daypartsContextLines,
    productRows,
    offerRows,
    rewardRows,
  } = useInvalidItemsModalDiagnostics({
    invalidCartEntries: cartEntries,
    invalidOfferEntries: offers,
    invalidRewardEntries: rewards,
  });

  const modalHeader = t('someItemsNotAvailable');
  const modalMessage = t(
    'weHadToRemoveUnavailableItems',
    'We had to remove unavailable items from your cart'
  );
  const modalAppearanceEventMessage = 'Minimal cart invalid items modal';

  const hasLoggedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasLoggedRef.current = false;
      return;
    }
    if (hasLoggedRef.current) {
      return;
    }
    hasLoggedRef.current = true;

    logRBIEvent({
      name: CustomEventNames.MODAL_APPEARANCE,
      type: EventTypes.Other,
      attributes: {
        ModalHeader: modalHeader,
        ModalMessage: modalMessage,
        isErrorModal: true,
        Message: modalAppearanceEventMessage,
        bodyMessage: modalMessage,
        modalHeader,
      },
    });

    logger.error({
      message: modalAppearanceEventMessage,
      ModalHeader: modalHeader,
      ModalMessage: modalMessage,
      isErrorModal: true,
    });
  }, [isOpen, modalHeader, modalMessage]);

  const headerNode = useMemo(
    () => (
      <Header title={modalHeader} variant="light" onCloseIconClick={noop}>
        <Typography variant="headerOne" color={primitive.$bbqBrown}>
          {modalHeader}
        </Typography>
      </Header>
    ),
    [modalHeader]
  );

  return (
    <InlineModal
      isOpen={isOpen}
      onClose={noop}
      backgroundColor={primitive.$white}
      header={headerNode}
      footer={
        <View style={styles.footer}>
          <ActionButton fullWidth onPress={onConfirm} testID="cart-invalid-items-confirm">
            {t('ok')}
          </ActionButton>
        </View>
      }
    >
      <ModalContent paddingTop="$4">
        <Typography style={styles.message}>{modalMessage}</Typography>
        <Section items={cartEntries} />
        <Section title={t('offers')} items={offers} />
        <Section title={t('rewards')} items={rewards} />
        {isDiagnosticsEnabled ? (
          <InvalidItemsDiagnosticsPanel
            daypartsContextLines={daypartsContextLines}
            productRows={productRows}
            offerRows={offerRows}
            rewardRows={rewardRows}
          />
        ) : null}
      </ModalContent>
    </InlineModal>
  );
};

const styles = StyleSheet.create({
  headerText: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 20,
    letterSpacing: -0.42,
  },
  message: {
    fontSize: 14,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    marginBottom: 16,
  },
  section: {
    marginBottom: 12,
  },
  entries: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 12,
    textTransform: 'uppercase',
    fontFamily: primitive.fontFamily.$headers,
    ...iosCartBoldWeight,
    color: primitive.$bbqBrown,
    marginBottom: 6,
  },
  footer: {
    width: '100%',
  },
});
